import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Theme definitions with metadata
export const THEMES = {
  magical: {
    id: 'magical',
    name: { en: 'Magical', de: 'Magisch' },
    description: { 
      en: 'Pink & playful - perfect for fairy tales', 
      de: 'Rosa & verspielt - perfekt für Märchen' 
    },
    icon: '✨',
    preview: {
      primary: '#FF69B4',
      secondary: '#9333EA',
      bg: '#FFF5F8'
    }
  },
  modern: {
    id: 'modern',
    name: { en: 'Modern', de: 'Modern' },
    description: { 
      en: 'Dark & sleek - contemporary style', 
      de: 'Dunkel & elegant - zeitgenössischer Stil' 
    },
    icon: '🌙',
    preview: {
      primary: '#8B5CF6',
      secondary: '#06B6D4',
      bg: '#0F0F1A'
    }
  },
  neutral: {
    id: 'neutral',
    name: { en: 'Neutral', de: 'Neutral' },
    description: { 
      en: 'Teal & friendly - for everyone', 
      de: 'Türkis & freundlich - für alle' 
    },
    icon: '🌈',
    preview: {
      primary: '#14B8A6',
      secondary: '#F59E0B',
      bg: '#F0FDFA'
    }
  }
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('magical'); // Default theme
  const [loading, setLoading] = useState(true);

  // Load theme from server on mount
  useEffect(() => {
    const loadTheme = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/theme`);
        const serverTheme = response.data?.theme || 'magical';
        setTheme(serverTheme);
        document.documentElement.setAttribute('data-theme', serverTheme);
      } catch (error) {
        console.log('Using default theme');
        document.documentElement.setAttribute('data-theme', 'magical');
      } finally {
        setLoading(false);
      }
    };

    loadTheme();
  }, []);

  // Apply theme to document when it changes
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // Also save to localStorage as backup
    localStorage.setItem('storytales-theme', theme);
  }, [theme]);

  // Update theme (admin only)
  const updateTheme = async (newTheme, token) => {
    try {
      await axios.put(
        `${API}/api/admin/settings/theme`,
        { theme: newTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTheme(newTheme);
      return { success: true };
    } catch (error) {
      console.error('Failed to update theme:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to update theme' };
    }
  };

  const value = {
    theme,
    setTheme,
    updateTheme,
    themes: THEMES,
    loading
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Helper to get email template colors based on theme
export function getEmailThemeColors(themeName) {
  const themes = {
    magical: {
      primary: '#FF69B4',
      primaryDark: '#FF1493',
      secondary: '#9333EA',
      bgLight: '#FFF5F8',
      bgGradient: 'linear-gradient(135deg, #FF69B4 0%, #FF1493 100%)',
      textDark: '#1F2937',
      textLight: '#ffffff'
    },
    modern: {
      primary: '#8B5CF6',
      primaryDark: '#7C3AED',
      secondary: '#06B6D4',
      bgLight: '#1A1A2E',
      bgGradient: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
      textDark: '#F9FAFB',
      textLight: '#ffffff'
    },
    neutral: {
      primary: '#14B8A6',
      primaryDark: '#0D9488',
      secondary: '#F59E0B',
      bgLight: '#F0FDFA',
      bgGradient: 'linear-gradient(135deg, #14B8A6 0%, #06B6D4 100%)',
      textDark: '#1F2937',
      textLight: '#ffffff'
    }
  };
  
  return themes[themeName] || themes.magical;
}
